import { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import "../../assets/scss/details/listApprovedModal.scss";
import {
  LdsButton,
  LdsModal,
  LdsLoadingSpinner,
  LdsIcon,
} from "@elilillyco/ux-lds-react";
import Select from "react-select";

const EditParticipantsModal = ({
  isOpen,
  setIsOpen,
  specialtyList,
  onSave,
}) => {
  const { userTableList, loading } = useSelector(({ admin }) => admin);
  const isMLRList = !specialtyList?.list_quarter || specialtyList?.list_quarter?.trim() === "";

  const [selectedReviewers, setSelectedReviewers] = useState([]);
  const [selectedApprovers, setSelectedApprovers] = useState([]);

  // UUIDs of users who have already acted — these must never be removed
  const reviewedUuids = specialtyList?.reviewsubmitted || [];
  const approvedUuids = specialtyList?.approversubmitted || [];

  // Separation of Duties: the list's creator cannot also be assigned as its
  // approver/reviewer, regardless of role.
  // Use email_id-based exclusion (not UUID) because the same person can have
  // multiple role entries in userTableList, each with a different UUID.
  const creatorUuid = typeof specialtyList?.created_by === "object"
    ? specialtyList?.created_by?.uuid
    : specialtyList?.created_by;
  const creatorEmail = typeof specialtyList?.created_by === "object"
    ? specialtyList?.created_by?.email_id
    : userTableList.find((u) => u.uuid === creatorUuid)?.email_id;
  const excludedCreatorEmail = creatorEmail || null;

  // Build reviewer options: active MLR reviewers + any already-reviewed users (locked)
  const activeReviewerSet = new Set(
    userTableList
      .filter((u) => u.active && u.role === "spclty_hub_mlr_reviewer" && u.email_id !== excludedCreatorEmail)
      .map((u) => u.uuid)
  );
  const reviewerOptions = [
    // Already-reviewed users not in the active list — show as locked so they're visible
    ...reviewedUuids
      .filter((id) => !activeReviewerSet.has(id))
      .map((id) => {
        const u = userTableList.find((u2) => u2.uuid === id);
        return u
          ? { label: `${u.first_name} ${u.last_name} (${u.email_id}) — Reviewed`, value: u.uuid, isDisabled: true }
          : { label: `Unknown (${id}) — Reviewed`, value: id, isDisabled: true };
      }),
    // Active MLR reviewers — mark already-reviewed ones as locked
    ...userTableList
      .filter((u) => u.active && u.role === "spclty_hub_mlr_reviewer" && u.email_id !== excludedCreatorEmail)
      .map((u) => ({
        label: reviewedUuids.includes(u.uuid)
          ? `${u.first_name} ${u.last_name} (${u.email_id}) — Reviewed`
          : `${u.first_name} ${u.last_name} (${u.email_id})`,
        value: u.uuid,
        isDisabled: reviewedUuids.includes(u.uuid),
      })),
  ];

  // Build approver options: active approvers + any already-approved users (locked)
  const activeApproverSet = new Set(
    userTableList
      .filter((u) => {
        if (!u.active || u.email_id === excludedCreatorEmail) return false;
        return specialtyList?.list_quarter
          ? u.role === "spclty_hub_incentivized_approver"
          : u.role === "spclty_hub_mlr_approver";
      })
      .map((u) => u.uuid)
  );
  const approverOptions = [
    // Already-approved users not in the active list — show as locked
    ...approvedUuids
      .filter((id) => !activeApproverSet.has(id))
      .map((id) => {
        const u = userTableList.find((u2) => u2.uuid === id);
        return u
          ? { label: `${u.first_name} ${u.last_name} (${u.email_id}) — Approved`, value: u.uuid, isDisabled: true }
          : { label: `Unknown (${id}) — Approved`, value: id, isDisabled: true };
      }),
    // Active approvers — mark already-approved ones as locked
    ...userTableList
      .filter((u) => {
        if (!u.active || u.email_id === excludedCreatorEmail) return false;
        return specialtyList?.list_quarter
          ? u.role === "spclty_hub_incentivized_approver"
          : u.role === "spclty_hub_mlr_approver";
      })
      .map((u) => ({
        label: approvedUuids.includes(u.uuid)
          ? `${u.first_name} ${u.last_name} (${u.email_id}) — Approved`
          : `${u.first_name} ${u.last_name} (${u.email_id})`,
        value: u.uuid,
        isDisabled: approvedUuids.includes(u.uuid),
      })),
  ];

  // Populate selections from current specialtyList data when modal opens
  useEffect(() => {
    if (isOpen && specialtyList) {
      setSelectedReviewers(specialtyList.reviewerlist || []);
      setSelectedApprovers(specialtyList.approverlist || []);
    }
  }, [isOpen, specialtyList]);

  const handleClose = (open) => {
    if (!open) {
      setSelectedReviewers([]);
      setSelectedApprovers([]);
    }
    setIsOpen(open);
  };

  const handleSave = () => {
    const payload = {};
    // Reviewerlist: only modifiable in REQUEST FOR REVIEW
    if (isMLRList && specialtyList?.list_status === "REQUEST FOR REVIEW") {
      payload.reviewerlist = selectedReviewers;
    }
    // Approverlist: only modifiable in REQUEST FOR APPROVAL
    if (specialtyList?.list_status === "REQUEST FOR APPROVAL") {
      payload.approverlist = selectedApprovers;
    }
    onSave(payload);
    setIsOpen(false);
  };

  // Show reviewer section only when reviewerlist changes are allowed
  const showReviewerSection =
    isMLRList && specialtyList?.list_status === "REQUEST FOR REVIEW";
  // Show approver section only when approverlist changes are allowed
  const showApproverSection =
    specialtyList?.list_status === "REQUEST FOR APPROVAL";

  const customSelectStyles = {
    control: (provided, state) => ({
      ...provided,
      minHeight: "40px",
      borderColor: state.isFocused ? "#0071ce" : "#ccc",
      boxShadow: state.isFocused ? "0 0 0 1px #0071ce" : "none",
      "&:hover": { borderColor: "#0071ce" },
    }),
    multiValue: (provided) => ({
      ...provided,
      backgroundColor: "#e6f2ff",
      borderRadius: "4px",
    }),
    multiValueLabel: (provided) => ({
      ...provided,
      color: "#0071ce",
      fontWeight: "500",
    }),
    multiValueRemove: (provided) => ({
      ...provided,
      color: "#0071ce",
      ":hover": { backgroundColor: "#0071ce", color: "white" },
    }),
    menu: (provided) => ({ ...provided, zIndex: 9999 }),
    option: (provided, state) => ({
      ...provided,
      backgroundColor: state.isSelected
        ? "#0071ce"
        : state.isFocused
        ? "#e6f2ff"
        : "white",
      color: state.isSelected ? "white" : "#333",
      "&:active": { backgroundColor: "#0071ce" },
    }),
  };

  return (
    <LdsModal
      modalId="edit-participants-modal"
      open={isOpen}
      setModalOpen={handleClose}
    >
      <div>
        <div className="icon-size">
          <LdsIcon name="UsersFill" />
        </div>
        <div className="msg-data">
          <h2>Edit Reviewers / Approvers</h2>
          <p style={{ color: "#666", fontSize: "14px", marginTop: "4px" }}>
            The list status will not change. Completed reviews/approvals by
            retained users will be preserved.
          </p>
        </div>

        {showReviewerSection && (
          <div className="msg-data">
            <div className="approver-selection-section">
              <div className="selection-header">
                <label className="selection-label">
                  Reviewers <span className="required-asterisk">*</span>
                </label>
                <LdsButton
                  linkButton
                  onClick={() => {
                    const selectableCount = reviewerOptions.filter((o) => !o.isDisabled).length;
                    const selectedSelectableCount = selectedReviewers.filter(
                      (id) => !reviewedUuids.includes(id)
                    ).length;
                    if (selectedSelectableCount === selectableCount) {
                      // Unselect All: keep only already-reviewed (locked) ones
                      setSelectedReviewers([...reviewedUuids]);
                    } else {
                      // Select All: include all options + already-reviewed
                      setSelectedReviewers([...new Set([
                        ...reviewerOptions.map((o) => o.value),
                        ...reviewedUuids,
                      ])]);
                    }
                  }}
                  disabled={loading || reviewerOptions.length === 0}
                >
                  {selectedReviewers.filter((id) => !reviewedUuids.includes(id)).length ===
                  reviewerOptions.filter((o) => !o.isDisabled).length
                    ? "Unselect All"
                    : "Select All"}
                </LdsButton>
              </div>
              {!loading ? (
                <Select
                  isMulti
                  options={reviewerOptions}
                  value={reviewerOptions.filter((o) =>
                    selectedReviewers.includes(o.value)
                  )}
                  onChange={(opts) => {
                    const selected = opts ? opts.map((o) => o.value) : [];
                    // Always retain already-reviewed reviewers — they cannot be removed
                    setSelectedReviewers([...new Set([...selected, ...reviewedUuids])]);
                  }}
                  placeholder="Search and select reviewers..."
                  styles={customSelectStyles}
                  isClearable
                  isSearchable
                  closeMenuOnSelect
                  className="react-select-container"
                  classNamePrefix="react-select"
                />
              ) : (
                <LdsLoadingSpinner size={40} />
              )}
              {selectedReviewers.length > 0 && (
                <div className="selection-count">
                  {selectedReviewers.length} reviewer
                  {selectedReviewers.length !== 1 ? "s" : ""} selected
                </div>
              )}
            </div>
          </div>
        )}

        {showApproverSection && (
          <div className="msg-data">
            <div
              className="approver-selection-section"
              style={{ marginTop: showReviewerSection ? "16px" : "0" }}
            >
              <div className="selection-header">
                <label className="selection-label">
                  Approvers <span className="required-asterisk">*</span>
                </label>
                <LdsButton
                  linkButton
                  onClick={() => {
                    const selectableCount = approverOptions.filter((o) => !o.isDisabled).length;
                    const selectedSelectableCount = selectedApprovers.filter(
                      (id) => !approvedUuids.includes(id)
                    ).length;
                    if (selectedSelectableCount === selectableCount) {
                      // Unselect All: keep only already-approved (locked) ones
                      setSelectedApprovers([...approvedUuids]);
                    } else {
                      // Select All: include all options + already-approved
                      setSelectedApprovers([...new Set([
                        ...approverOptions.map((o) => o.value),
                        ...approvedUuids,
                      ])]);
                    }
                  }}
                  disabled={loading || approverOptions.length === 0}
                >
                  {selectedApprovers.filter((id) => !approvedUuids.includes(id)).length ===
                  approverOptions.filter((o) => !o.isDisabled).length
                    ? "Unselect All"
                    : "Select All"}
                </LdsButton>
              </div>
              {!loading ? (
                <Select
                  isMulti
                  options={approverOptions}
                  value={approverOptions.filter((o) =>
                    selectedApprovers.includes(o.value)
                  )}
                  onChange={(opts) => {
                    const selected = opts ? opts.map((o) => o.value) : [];
                    // Always retain already-approved approvers — they cannot be removed
                    setSelectedApprovers([...new Set([...selected, ...approvedUuids])]);
                  }}
                  placeholder="Search and select approvers..."
                  styles={customSelectStyles}
                  isClearable
                  isSearchable
                  closeMenuOnSelect
                  className="react-select-container"
                  classNamePrefix="react-select"
                />
              ) : (
                <LdsLoadingSpinner size={40} />
              )}
              {selectedApprovers.length > 0 && (
                <div className="selection-count">
                  {selectedApprovers.length} approver
                  {selectedApprovers.length !== 1 ? "s" : ""} selected
                </div>
              )}
            </div>
          </div>
        )}

        <div className="row">
          <div className="col">
            <span className="floatRight mt-4">
              <span className="mr-4">
                <LdsButton
                  classes="button-icon-left blue-bdr-btn"
                  onClick={() => handleClose(false)}
                >
                  Cancel
                </LdsButton>
              </span>
              <LdsButton
                classes="button-icon-left blue-btn"
                onClick={handleSave}
                disabled={
                  (showReviewerSection && selectedReviewers.length === 0) ||
                  (showApproverSection && selectedApprovers.length === 0)
                }
              >
                Save
              </LdsButton>
            </span>
          </div>
        </div>
      </div>
    </LdsModal>
  );
};

export default EditParticipantsModal;
